import React from "react";

import {
  createAccordionItems,
  isFootprintReadOnly,
  useDairyMethodology,
  useGetCarbonProgramByFarmId,
  useGetFootprintingDairyFootprint,
  useNavigate,
  useTranslation,
  useWasAutofilled,
} from "utils";

import { FormFlowScreen, FormPage } from "components";

import { AutofillBanner } from "pages/app/footprinting/components/AutofillBanner";
import { DairyGettingStartedForm } from "pages/app/footprinting/components/formFlows/dairyFlow/DairyGettingStartedForm";
import DairyRefrigerantsForm from "pages/app/footprinting/components/formFlows/dairyFlow/DairyRefrigerantsForm";
import { FeedAdditiveForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedAdditiveForm";
import { FeedForageLossForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedForageLossForm";
import { FeedInformationForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedInformationForm";
import { FeedPastureForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedPastureForm";
import { CommercialClassesForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdCommercialClasses";
import { HerdConditionForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdConditionForm";
import { HerdDetailsForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdDetailsForm";
import { HerdOffspringForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdOffspringForm";
import { HerdSizeForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdSizeForm";
import { ManureAdditiveForm } from "pages/app/footprinting/components/formFlows/dairyFlow/ManureAdditiveForm";
import { ManureStorageForm } from "pages/app/footprinting/components/formFlows/dairyFlow/ManureStorageForm";
import { MilkYieldForm } from "pages/app/footprinting/components/formFlows/dairyFlow/MilkYieldForm";
import { MoorlandForm } from "pages/app/footprinting/components/formFlows/dairyFlow/MoorlandForm";
import { DairyCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface DairyFlowProps {
  farmId: string;
  year: number;
  initialStep?: number;
  editMode?: boolean;
  enablePageTransitions?: boolean;
}

const dairyFormCategories = {
  animals: "footprinting:footprintingLandingPage.assessmentCategories.dairy.animalsBreed",
  feed: "footprinting:footprintingLandingPage.assessmentCategories.dairy.feed",
  manure: "footprinting:footprintingLandingPage.assessmentCategories.dairy.manure",
};

export const DairyFormFlow = ({ farmId, year, initialStep, editMode, enablePageTransitions }: DairyFlowProps) => {
  const { t } = useTranslation("footprintingDairy");
  const navigate = useNavigate();
  const dairyProgressQuery = useGetFootprintingDairyFootprint(farmId, year);
  const readOnly = isFootprintReadOnly(dairyProgressQuery.data);
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const { isCfp } = useDairyMethodology(farmId);
  const wasAutofilled = useWasAutofilled("dairy", farmId, year);
  const [currentStep, setCurrentStep] = React.useState<number | undefined>(initialStep ?? 0);

  const getPageStatus = (pageKey: string) => {
    if (dairyProgressQuery.data?.pagesCompleted.includes(pageKey)) return "complete";
    if (dairyProgressQuery.data?.pagesIncomplete.includes(pageKey)) return "incomplete";
    return null;
  };

  const dairyFlowSteps: FormPage[] = [
    {
      navigationTitle: t("footprinting:navigation.dairy.gettingStarted"),
      key: DairyGettingStartedForm.Key,
      status: getPageStatus(DairyGettingStartedForm.Key),
      pageSubtitle: t("dairyGettingStartedForm.subTitle"),
      body: () => <DairyGettingStartedForm primaryDataId={dairyProgressQuery.data?.footprint?.id} includeRefrigeration={isCfp} />,
      accordionFooter: wasAutofilled ? <AutofillBanner /> : undefined,
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.milkYield"),
      key: MilkYieldForm.Key,
      status: getPageStatus(MilkYieldForm.Key),
      pageSubtitle: t("dairyMilkYieldForm.subTitle"),
      body: () => <MilkYieldForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "milkYield"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.herdSize"),
      navigationCategory: t(dairyFormCategories.animals),
      key: HerdSizeForm.Key,
      status: getPageStatus(HerdSizeForm.Key),
      pageSubtitle: t("dairyHerdSizeForm.subTitle", { year }),
      body: () => <HerdSizeForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "herdSize"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.herdDetails"),
      navigationCategory: t(dairyFormCategories.animals),
      key: HerdDetailsForm.Key,
      status: getPageStatus(HerdDetailsForm.Key),
      pageSubtitle: t("dairyHerdDetailsForm.subTitle"),
      body: () => <HerdDetailsForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "herdDetails"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.herdCondition"),
      navigationCategory: t(dairyFormCategories.animals),
      key: HerdConditionForm.Key,
      status: getPageStatus(HerdConditionForm.Key),
      pageSubtitle: t("dairyHerdConditionForm.subTitle"),
      body: () => <HerdConditionForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "herdCondition"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.herdOffspring"),
      navigationCategory: t(dairyFormCategories.animals),
      key: HerdOffspringForm.Key,
      status: getPageStatus(HerdOffspringForm.Key),
      pageSubtitle: t("dairyHerdOffspringForm.subTitle"),
      body: () => <HerdOffspringForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "herdOffspring"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.herdCommercialClasses"),
      navigationCategory: t(dairyFormCategories.animals),
      key: CommercialClassesForm.Key,
      status: getPageStatus(CommercialClassesForm.Key),
      pageSubtitle: t("dairyHerdCommercialClassesForm.subTitle"),
      body: () => (
        <CommercialClassesForm
          readOnly={readOnly}
          primaryDataId={dairyProgressQuery.data?.footprint?.id}
          farmId={farmId}
          year={year}
          onNavigateToStep={setCurrentStep}
        />
      ),
      accordionItems: createAccordionItems(t, "footprintingDairy", "herdCommercialClasses"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.feedPasture"),
      navigationCategory: t(dairyFormCategories.feed),
      key: FeedPastureForm.Key,
      status: getPageStatus(FeedPastureForm.Key),
      pageSubtitle: t("dairyFeedPastureForm.subTitle"),
      body: () => <FeedPastureForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "feedPasture"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.feedInformation"),
      navigationCategory: t(dairyFormCategories.feed),
      key: FeedInformationForm.Key,
      status: getPageStatus(FeedInformationForm.Key),
      pageSubtitle: t("dairyFeedTypesForm.subTitle"),
      body: () => <FeedInformationForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "feedInformation"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.additives"),
      navigationCategory: t(dairyFormCategories.feed),
      key: FeedAdditiveForm.Key,
      status: getPageStatus(FeedAdditiveForm.Key),
      pageSubtitle: t("dairyFeedAdditivesForm.subTitle"),
      body: () => <FeedAdditiveForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "feedAdditives"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.feedForageLoss"),
      navigationCategory: t(dairyFormCategories.feed),
      key: FeedForageLossForm.Key,
      status: getPageStatus(FeedForageLossForm.Key),
      pageSubtitle: t("dairyFeedForageLossForm.subTitle"),
      body: () => <FeedForageLossForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "feedForageLoss"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.manureStorage"),
      navigationCategory: t(dairyFormCategories.manure),
      key: ManureStorageForm.Key,
      status: getPageStatus(ManureStorageForm.Key),
      pageSubtitle: t("dairyManureStorageForm.subTitle"),
      body: () => <ManureStorageForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "manureStorage"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.manureAdditive"),
      navigationCategory: t(dairyFormCategories.manure),
      key: ManureAdditiveForm.Key,
      status: getPageStatus(ManureAdditiveForm.Key),
      pageSubtitle: t("dairyManureAdditiveForm.subTitle"),
      body: () => <ManureAdditiveForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "manureAdditive"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.refrigeration"),
      key: DairyRefrigerantsForm.Key,
      status: getPageStatus(DairyRefrigerantsForm.Key),
      pageSubtitle: t("dairyRefrigerantsForm.info"),
      body: () => <DairyRefrigerantsForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "refrigeration"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.moorland"),
      key: MoorlandForm.Key,
      status: getPageStatus(MoorlandForm.Key),
      pageSubtitle: t("dairyMoorlandForm.info"),
      body: () => <MoorlandForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingDairy", "moorland"),
    },
    {
      navigationTitle: t("footprinting:navigation.dairy.comment"),
      key: DairyCommentForm.Key,
      status: getPageStatus(DairyCommentForm.Key),
      pageSubtitle: t("footprinting:commentForm.subTitle"),
      body: () => <DairyCommentForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
  ];

  // Carbon programs can opt out of specific steps via excludedFormSteps. Excluded steps
  // are filtered here so they never render, and the server mirrors this by excluding
  // them from completion stats. To exclude a step, add its FormStepId to the carbon
  // program's excludedFormSteps array in consts/carbonPrograms.ts on the API.
  // LfL (the default dairy methodology) has no refrigerant inputs, so that step is CFP-only; mirrors
  // FootprintingUtils.dairyMethodologyExcludedFormSteps on the API.
  const excludedSteps = new Set<string>([
    ...(carbonProgramQuery.data?.farmLevelFootprinting.excludedFormSteps ?? []),
    ...(isCfp ? [] : [DairyRefrigerantsForm.Key]),
  ]);
  const enabledSteps = dairyFlowSteps.filter((step) => !excludedSteps.has(step.key));

  return (
    <FormFlowScreen
      formPages={enabledSteps}
      initialStep={currentStep}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      completionPercent={dairyProgressQuery.data?.completionPct ?? 0}
      navBarTitle={t("footprinting:navigation.dairy.navBarTitle", { year })}
      editMode={editMode}
      onClose={() => navigate("/footprinting", { state: { year } })}
      isLoading={dairyProgressQuery.isLoading}
      enablePageTransitions={enablePageTransitions}
    />
  );
};
